import axios from "axios";
import { useState, useEffect, useContext } from "react";
import { API_BASE_URL } from "../../api/config";
import { getCookie } from "../../api/csrf";

import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslateKey } from "../translation/useTranslateKey";
import { LangContext } from "../translation/langugeContext";
import Select from "react-select";
import {
  FaHome,
  FaPaperPlane,
  FaTimes,
  FaBolt,
  FaTag,
  FaMapMarkerAlt,
  FaMap,
  FaBuilding,
  FaCoins,
  FaHandHoldingUsd,
} from "react-icons/fa";

const AddUnitModal = ({ isOpen, onClose, pageData }) => {
  const [activeTab, setActiveTab] = useState("quick");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  const t = useTranslateKey();
  const { lang } = useContext(LangContext);
  const onFormSubmit = (data) => {
    if (activeTab === "quick") {
      axios
        .post(`${API_BASE_URL}/quick-sale-request/`, data, {
          headers: {
            "X-CSRFToken": getCookie("csrftoken"),
          },
          withCredentials: true,
        })
        .then((response) => {
          console.log("Quick Sales Form Submitted:", response.data);
          reset(); // reset form
          onClose(); // close modal
        })
        .catch((error) => {
          console.error("Error submitting quick sales form:", error);
        });
    } else {
      axios
        .post(`${API_BASE_URL}/normal-sale-request/`, data, {
          headers: {
            "X-CSRFToken": getCookie("csrftoken"),
          },
          withCredentials: true,
        })
        .then((response) => {
          console.log("Normal Sales Form Submitted:", response.data);
          reset();
          onClose();
        })
        .catch((error) => {
          console.error("Error submitting normal sales form:", error);
        });
    }
  };

  const quickSchema = Yup.object().shape({
    name: Yup.string()
      .required(t("error.requiredName"))
      .matches(/^[A-Za-z ]+$/, t("error.nameOnlyLetters")),
    mobile_number: Yup.string()
      .required(t("error.requiredPhone"))
      .matches(/^\d+$/, t("error.phoneDigitsOnly"))
      .matches(/^[0-9]{7,20}$/, t("error.invalidPhone")),
    location_city: Yup.string().required(t("error.requiredLocation")),
    location_area: Yup.string().required(t("error.requiredArea")),

    project: Yup.string(), //optional
    new_property_type: Yup.string().required(t("error.requiredPropertyType")),

    min_total_price: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required(t("error.requiredMinTotalPrice"))
      .positive(t("error.minTotalPricePositiveOnly"))
      .integer()
      .typeError(t("error.minTotalPriceNumberOnly"))
      .test(
        "maxDigits",
        t("error.minTotalPriceMaxDigits"),
        (value) => !value || value.toString().length <= 11
      ),
    down_payment: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === "") return undefined;
        return value;
      })
      .required(t("error.requiredDownPayment"))
      .positive(t("error.downPaymentPositiveOnly"))
      .integer()
      .typeError(t("error.downPaymentNumberOnly"))
      .test(
        "maxDigits",
        t("error.downPaymentMaxDigits"),
        (value) => !value || value.toString().length <= 11
      ),
  });
  const normalSchema = Yup.object().shape({
    name: Yup.string()
      .required(t("error.requiredName"))
      .matches(/^[A-Za-z ]+$/, t("error.nameOnlyLetters")),
    mobile_number: Yup.string()
      .required(t("error.requiredPhone"))
      .matches(/^\d+$/, t("error.phoneDigitsOnly"))
      .matches(/^[0-9]{7,20}$/, t("error.invalidPhone")),
    location_city: Yup.string().required(t("error.requiredLocation")),
    location_area: Yup.string().required(t("error.requiredArea")),

    project: Yup.string(), //optional
    new_property_type: Yup.string().required(t("error.requiredPropertyType")),

    price: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required(t("error.requiredPrice"))
      .positive(t("error.pricePositiveOnly"))
      .integer()
      .typeError(t("error.priceNumberOnly"))
      .test(
        "maxDigits",
        t("error.priceMaxDigits"),
        (value) => !value || value.toString().length <= 11
      ),
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
    register,
    watch,
    trigger, // to re-trigger validation
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(activeTab === "quick" ? quickSchema : normalSchema),
  });

  const selectedCityValue = watch("location_city");
  const [filteredAreas, setFilteredAreas] = useState([]);

  useEffect(() => {
    if (selectedCityValue && pageData && pageData.normalized_areas) {
      const areas = pageData.normalized_areas.filter(
        (area) => area.city_id?.toString() === selectedCityValue.toString()
      );
      setFilteredAreas(areas);
    } else {
      setFilteredAreas([]);
    }
  }, [selectedCityValue, pageData]);

  const handleCityChange = (selectedOption) => {
    const cityId = selectedOption?.value || "";
    setValue("location_city", cityId, { shouldValidate: true });
    setValue("location_area", "", { shouldValidate: true });
    trigger("location_city");
  };

  const formatNumberWithComma = (value) => {
    if (!value) return "";

    let formattedValue = value.replace(/[^\d]/g, "");

    return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // control the appear of scroller on main page and modal
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      opacity: state.isDisabled ? 0.6 : 1,
      backgroundColor: state.isDisabled
        ? "rgba(255, 255, 255, 0.4)"
        : state.isFocused
        ? "rgba(255, 255, 255, 0.15)"
        : "#11132F",
      border: state.isDisabled
        ? "1px solid #555"
        : state.isFocused
        ? "1px solid #f9c835"
        : "1px solid #332d7e",
      borderRadius: 8,
      color: "#fff",
      height: 45,
      paddingLeft: 16,
      paddingRight: 40,
      boxShadow: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: state.isFocused
          ? "rgba(255,255,255,0.15)"
          : "rgba(0,0,0,0.3)",
        borderColor: state.isFocused ? "#f9c835" : "rgba(255,255,255,0.2)",
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#aaa",
      position: "absolute",
      right: 12,
      padding: 0,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({
      ...base,
      backgroundColor: "#11132F",
      border: "1px solid #f9c835",
      borderRadius: 8,
      marginTop: 4,
      color: "#fff",
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,

      scrollbarColor: "#007ACC transparent", // scroller color
      scrollbarWidth: "thin",
    }),
    option: (base, state) => ({
      ...base,
      cursor: "pointer",
      color: "#fff",
      backgroundColor: state.isFocused ? "#007ACC" : "#11132f",
      textAlign: "left", // text left
      paddingLeft: "12px",
      ":active": { backgroundColor: "#007ACC" },
    }),
    singleValue: (base) => ({ ...base, color: "#fff" }),
    placeholder: (base) => ({ ...base, color: "#fff" }),
    valueContainer: (base) => ({ ...base, paddingLeft: 0 }),
  };

  const propertyTypeOptions = [
    {
      value: "",
      label: t("selectPropertyType"),
    },
    ...pageData.property_categories.map((category) => ({
      label: lang === "ar" ? category.category_ar : category.category_en,
      options: category.types.map((type) => ({
        value: type.id.toString(),
        label: lang === "ar" ? type.type_ar : type.type_en.charAt(0).toUpperCase() + type.type_en.slice(1),
      })),
    })),
  ];

  return (
    <div
      className="fixed inset-0 flex items-start justify-center z-50 pt-20 overflow-y-auto"
      style={{ backgroundColor: "rgba(18, 21, 37, 0.98)" }}
    >
      <div className="bg-[#11132F] border-2 border-[#332D7E] rounded-xl w-full max-w-4xl mx-4 relative shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header Section */}
        <div className="p-6 border-b border-[#332D7E]">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-white">
              {t("addYourUnit")}
            </h2>
            <button
              className="w-8 h-8 bg-[#F9C835] text-[#121525] rounded-full flex items-center justify-center hover:bg-[#332D7E] hover:text-white transition-all duration-300"
              onClick={() => {
                reset();
                onClose();
              }} // on close before submit reset data
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex bg-[#332D7E]/30 rounded-lg p-1">
            <button
              onClick={() => handleTabChange("quick")}
              className={`tab-btn flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 text-lg ${
                activeTab === "quick" ? "active-tab" : ""
              }`}
            >
              <FaBolt className="mr-2 text-red-500 inline" />
              {t("quickSales")}
            </button>
            <button
              onClick={() => handleTabChange("normal")}
              className={`tab-btn flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 text-lg ${
                activeTab === "normal" ? "active-tab" : ""
              }`}
            >
              <FaTag className="mr-2 inline" />
              {t("normalSales")}
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* Quick Sales Form */}
          {activeTab === "quick" && (
            <div id="quickSalesForm" className="tab-content">
              <div className="mb-3">
                <h3 className="text-2xl font-bold text-white">
                  <FaBolt className="text-primary-dark text-xl inline mr-2" />
                  {t("quickSales")}{" "}
                  <span className="text-sm font-normal text-gray-400">
                    {t("quickSalesSubtitle")}
                  </span>
                </h3>
              </div>

              <form
                onSubmit={handleSubmit(onFormSubmit)}
                className="space-y-3"
                id="quickSalesFormElement"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block mb-1" style={{ color: "#F9C835" }}>
                      {t("name")}
                    </label>
                    <input
                      {...register("name")}
                      className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block mb-1" style={{ color: "#F9C835" }}>
                      {t("mobileNumber")}  
                    </label>
                    <input
                      {...register("mobile_number")}
                      className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                    />
                    {errors.mobile_number && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.mobile_number.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* City selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Controller
                      name="location_city"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaMapMarkerAlt
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_city")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("selectCity") },
                              ...pageData.normalized_cities.map((city) => ({
                                value: city.id.toString(),
                                label: lang === "ar" ? city.name_ar : city.name_en
                              })),
                            ]}
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) => {
                              field.onChange(selected?.value || "");
                              handleCityChange(selected);
                            }}
                            value={
                              pageData.normalized_cities
                                .map((city) => ({
                                  value: city.id.toString(),
                                  label: lang === "ar" ? city.name_ar : city.name_en,
                                }))
                                .find((c) => c.value === field.value) || {
                                value: "",
                                label: t("selectCity"),
                              }
                            }
                            placeholder = {t("selectCity")}
                            isSearchable={false}
                          />
                          {errors.location_city && (
                            <span className="text-red-500">
                              {errors.location_city.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Area selection */}
                  <div>
                    <Controller
                      name="location_area"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaMap
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_area")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("select_area") },
                              ...filteredAreas.map((area) => ({
                                value: area.id.toString(),
                                label: lang === "ar" ? area.name_ar : area.name_en
                              })),
                            ]}
                            isDisabled={
                              !selectedCityValue || filteredAreas.length === 0
                            }
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            value={
                              filteredAreas
                                .map((area) => ({
                                  value: area.id.toString(),
                                  label: lang === "ar" ? area.name_ar : area.name_en
                                }))
                                .find((a) => a.value === field.value) || {
                                value: "",
                                label: t("select_area"),
                              }
                            }
                            placeholder={t("select_area")}
                            isSearchable={false}
                          />
                          {errors.location_area && (
                            <span className="text-red-500">
                              {errors.location_area.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Project selection */}
                  <div>
                    <Controller
                      name="project"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaBuilding
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_project")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("selectProject") },
                              ...pageData.projects.map((project) => ({
                                value: project.id.toString(),
                                label: lang === "ar" ? project.name_ar : project.name,
                              })),
                            ]}
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            value={
                              pageData.projects
                                .map((project) => ({
                                  value: project.id.toString(),
                                  label: lang === "ar" ? project.name_ar : project.name,
                                }))
                                .find((p) => p.value === field.value) || {
                                value: "",
                                label: t("selectProject"),
                              }
                            }
                            placeholder={t("selectProject")}
                            isSearchable={false}
                          />
                          {errors.project && (
                            <span className="text-red-500">
                              {errors.project.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Property Type */}
                  <div>
                    <Controller
                      name="new_property_type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaHome
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_propertyType")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={propertyTypeOptions}
                            styles={{
                              ...selectStyles,
                              groupHeading: (base) => ({
                                ...base,
                                backgroundColor: "#11132f",
                                color: "#f9c835",
                                padding: "4px 8px",
                                fontWeight: "bold",
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            value={
                              propertyTypeOptions
                                .flatMap((group) =>
                                  group.options ? group.options : [group]
                                )
                                .find((opt) => opt.value === field.value) ||
                              null
                            }
                            placeholder= {t("selectPropertyType")}
                            isSearchable={false}
                          />
                          {errors.new_property_type && (
                            <span className="text-red-500">
                              {errors.new_property_type.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Min Total Price */}
                  <div>
                    <div className="space-y-2">
                      <div>
                        <label
                          className="block mb-1"
                          style={{ color: "#F9C835" }}
                        >
                          <FaCoins
                            className="mr-2 inline"
                            style={{ color: "#F9C835" }}
                          />
                          {t("minTotalPrice")} *{" "}
                          <span style={{ color: "white" }}>
                            {t("priceTag")}
                          </span>
                        </label>
                        <input
                          type="text"
                          value={formatNumberWithComma(
                            watch("min_total_price") || ""
                          )}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");
                            if (!isNaN(rawValue)) {
                              setValue("min_total_price", rawValue, {
                                shouldValidate: true,
                              });
                            }
                          }}
                          className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                          required
                        />
                        {errors.min_total_price && (
                          <span className="text-red-500">
                            {errors.min_total_price.message}
                          </span>
                        )}
                      </div>

                      {/* Down Payment */}
                      <div>
                        <label
                          className="block mb-1"
                          style={{ color: "#F9C835" }}
                        >
                          <FaHandHoldingUsd
                            className="mr-2 inline"
                            style={{ color: "#F9C835" }}
                          />
                          {t("downPayment")} *
                        </label>
                        <input
                          type="text"
                          value={formatNumberWithComma(
                            watch("down_payment") || ""
                          )}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");
                            if (!isNaN(rawValue)) {
                              setValue("down_payment", rawValue, {
                                shouldValidate: true,
                              });
                            }
                          }}
                          className="search-input w-full text-sm py-1 px-2 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                          required
                        />
                        {errors.down_payment && (
                          <span className="text-red-500">
                            {errors.down_payment.message}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* Normal Sales Form */}
          {activeTab === "normal" && (
            <div id="normalSalesForm" className="tab-content">
              <div className="mb-3">
                <h3 className="text-2xl font-bold text-white">
                  <FaTag className="text-primary-dark text-xl inline mr-2" />
                  {t("normalSales")}
                </h3>
              </div>
              <form
                onSubmit={handleSubmit(onFormSubmit)}
                className="space-y-3"
                id="normalSalesFormElement"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block mb-1" style={{ color: "#F9C835" }}>
                      {t("name")} *
                    </label>
                    <input
                      {...register("name")}
                      className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block mb-1" style={{ color: "#F9C835" }}>
                      {t("mobileNumber")} *
                    </label>
                    <input
                      {...register("mobile_number")}
                      className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                    />
                    {errors.mobile_number && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.mobile_number.message}
                      </p>
                    )}
                  </div>
                </div>
                {/* City selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Controller
                      name="location_city"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaMapMarkerAlt
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_city")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("selectCity") },
                              ...pageData.normalized_cities.map((city) => ({
                                value: city.id.toString(),
                                label: lang === "ar" ? city.name_ar : city.name_en
                              })),
                            ]}
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) => {
                              field.onChange(selected?.value || "");
                              handleCityChange(selected);
                            }}
                            value={
                              pageData.normalized_cities
                                .map((city) => ({
                                  value: city.id.toString(),
                                  label: lang === "ar" ? city.name_ar : city.name_en,
                                }))
                                .find((c) => c.value === field.value) || {
                                value: "",
                                label: t("selectCity"),
                              }
                            }
                            placeholder={t("selectCity")}
                            isSearchable={false}
                          />
                          {errors.location_city && (
                            <span className="text-red-500">
                              {errors.location_city.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Area selection */}
                  <div>
                    <Controller
                      name="location_area"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaMap
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_area")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("select_area") },
                              ...filteredAreas.map((area) => ({
                                value: area.id.toString(),
                                label: lang === "ar" ? area.name_ar : area.name_en
                              })),
                            ]}
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isDisabled={
                              !selectedCityValue || filteredAreas.length === 0
                            }
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            value={
                              filteredAreas
                                .map((area) => ({
                                  value: area.id.toString(),
                                  label: lang === "ar" ? area.name_ar : area.name_en,
                                }))
                                .find((a) => a.value === field.value) || {
                                value: "",
                                label: t("select_area"),
                              }
                            }
                            placeholder= {t("select_area")}
                            isSearchable={false}
                          />
                          {errors.location_area && (
                            <span className="text-red-500">
                              {errors.location_area.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>

                  {/* Project selection */}
                  <div>
                    <Controller
                      name="project"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label
                            className="block mb-1"
                            style={{ color: "#F9C835" }}
                          >
                            <FaBuilding
                              className="mr-2 inline"
                              style={{ color: "#F9C835" }}
                            />
                            {t("form_project")}
                          </label>
                          <Select
                            {...field}
                            className="w-full"
                            classNamePrefix="react-select"
                            options={[
                              { value: "", label: t("selectProject") },
                              ...pageData.projects.map((project) => ({
                                value: project.id.toString(),
                                label: lang === "ar" ? project.name_ar : project.name,
                              })),
                            ]}
                            styles={{
                              ...selectStyles,
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selected) =>
                              field.onChange(selected?.value || "")
                            }
                            value={
                              pageData.projects
                                .map((project) => ({
                                  value: project.id.toString(),
                                  label: lang === "ar" ? project.name_ar : project.name,
                                }))
                                .find((p) => p.value === field.value) || {
                                value: "",
                                label: t("selectProject"),
                              }
                            }
                            placeholder={t("selectProject")}
                            isSearchable={false}
                          />
                          {errors.project && (
                            <span className="text-red-500">
                              {errors.project.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Property Type */}
                    <div>
                      <Controller
                        name="new_property_type"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <div>
                            <label
                              className="block mb-1"
                              style={{ color: "#F9C835" }}
                            >
                              <FaHome
                                className="mr-2 inline"
                                style={{ color: "#F9C835" }}
                              />
                              {t("form_propertyType")}
                            </label>
                            <Select
                              {...field}
                              className="w-full"
                              classNamePrefix="react-select"
                              options={propertyTypeOptions}
                              styles={{
                                ...selectStyles,
                                groupHeading: (base) => ({
                                  ...base,
                                  backgroundColor: "#11132f",
                                  color: "#f9c835",
                                  padding: "4px 8px",
                                  fontWeight: "bold",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              onChange={(selected) =>
                                field.onChange(selected?.value || "")
                              }
                              value={
                                propertyTypeOptions
                                  .flatMap((group) =>
                                    group.options ? group.options : [group]
                                  )
                                  .find((opt) => opt.value === field.value) ||
                                null
                              }
                              placeholder={t("selectPropertyType")}
                              isSearchable={false}
                            />
                            {errors.new_property_type && (
                              <span className="text-red-500">
                                {errors.new_property_type.message}
                              </span>
                            )}
                          </div>
                        )}
                      />
                    </div>

                    <div>
                      <label
                        className="block mb-1 whitespace-nowrap"
                        style={{ color: "#F9C835" }}
                      >
                        <FaCoins
                          className="mr-2 inline"
                          style={{ color: "#F9C835" }}
                        />
                        {t("totalPriceLabel")}{" "}
                        <span style={{ color: "white" }}>{t("priceTag")}</span>
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithComma(watch("price") || "")}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/,/g, "");

                          if (!isNaN(rawValue)) {
                            setValue("price", rawValue, {
                              shouldValidate: true,
                            });
                          }
                        }}
                        className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                        required
                      />
                      {errors.price && (
                        <span className="text-red-500">
                          {errors.price.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Fixed Footer with Submit Button */}
        <div className="border-t border-[#332D7E] bg-[#11132F]">
          <button
            type="button"
            onClick={handleSubmit(onFormSubmit)}
            className="w-full bg-[#F9C835] hover:bg-[#332D7E] text-[#121525] hover:text-white px-6 py-4 rounded-lg transition-all duration-300 font-semibold text-lg flex items-center justify-center gap-2"
          >
            <FaPaperPlane />
            <span>{t("submit")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUnitModal;
